import React, { useState, useEffect, useMemo, useCallback, Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import { FileText, Mail, Send, Download, Save, Settings, Users, AlertCircle, Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

// Import custom hooks
import { useDocumentData, useTemplateSelection, useDocumentManagement } from '../../hooks/useDocumentData';
import { useEmployeeManagement, useEmailOperations } from '../../hooks/useEmployeeManagement';
import { useDocumentGeneration, useDocumentCache } from '../../hooks/useDocumentGeneration';

// Lazy load tab components for better performance
const DocumentTemplatesTab = lazy(() => import('./tabs/DocumentTemplatesTab'));
const GenerateDocumentTab = lazy(() => import('./tabs/GenerateDocumentTab'));
const CustomLetterTab = lazy(() => import('./tabs/CustomLetterTab'));

// Error Boundary Component
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('DocumentBuilder Error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            const { theme } = this.props;
            return (
                <div className={`p-6 rounded-lg ${
                    theme === 'dark' ? 'bg-red-900/20 border border-red-700' : 'bg-red-50 border border-red-200'
                }`}>
                    <div className="flex items-center gap-3">
                        <AlertCircle className="w-6 h-6 text-red-600" />
                        <div>
                            <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Something went wrong
                            </h3>
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                {this.state.error?.message || 'An unexpected error occurred'}
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Loading Spinner Component
const LoadingSpinner = ({ theme }) => (
    <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className={`ml-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Loading...
        </span>
    </div>
);

// Tab Button Component
const TabButton = ({ id, label, icon: Icon, isActive, onClick, theme, badge }) => (
    <button
        id={`${id}-tab`}
        onClick={() => onClick(id)}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            isActive
                ? theme === 'dark'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-blue-600 text-white shadow-lg'
                : theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700'
                    : 'text-gray-600 hover:bg-gray-100'
        }`}
        role="tab"
        aria-selected={isActive}
        aria-controls={`${id}-panel`}
    >
        <Icon className="w-4 h-4" />
        <span className="hidden sm:inline">{label}</span>
        {badge !== undefined && (
            <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                badge > 0
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-300 text-gray-600'
            }`}>
                {badge || 0}
            </span>
        )}
    </button>
);

const DocumentBuilderRefactored = () => {
    const { theme } = useTheme();
    const [activeTab, setActiveTab] = useState('templates');
    const tabLocation = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(tabLocation.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);
    }, [tabLocation.search]);

    // Initialize custom hooks
    const documentData = useDocumentData();
    const templateSelection = useTemplateSelection(documentData.categories);
    const documentManagement = useDocumentManagement();
    const employeeManagement = useEmployeeManagement();
    const emailOperations = useEmailOperations();
    const documentGeneration = useDocumentGeneration();
    const documentCache = useDocumentCache();

    // Local state
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [generatingDocument, setGeneratingDocument] = useState(null);
    const [generateLoading, setGenerateLoading] = useState(false);
    const [emailSubject, setEmailSubject] = useState('');
    const [emailMessage, setEmailMessage] = useState('');
    const [customLetterSubject, setCustomLetterSubject] = useState('');
    const [customLetterContent, setCustomLetterContent] = useState('');
    const [customLetterRecipients, setCustomLetterRecipients] = useState([]);
    const [customLetterPreview, setCustomLetterPreview] = useState(false);
    const [companyData, setCompanyData] = useState(null);

    // Tab configuration
    const tabs = useMemo(() => [
        { id: 'templates', label: 'Templates', icon: FileText },
        { id: 'generate', label: 'Generate', icon: Send },
        { id: 'custom', label: 'Custom Letter', icon: Mail }
    ], []);

    // Initialize data on mount
    useEffect(() => {
        employeeManagement.fetchEmployees();
        documentCache.cleanExpiredCache();
    }, []);

    // Fetch company data for document generation
    useEffect(() => {
        const fetchCompanyData = async () => {
            try {
                const response = await fetch('/api/companies/me', {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    let company = data.company || data;

                    // Check company settings for logo if not in companies table
                    if (!company.logo_url) {
                        try {
                            const settingsResponse = await fetch('/api/company/settings', {
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                            });
                            if (settingsResponse.ok) {
                                const settingsResult = await settingsResponse.json();
                                // API returns { success: true, data: { logo, name, address, ... } }
                                const settings = settingsResult.data || settingsResult;

                                console.log('🔍 [DEBUG] Company settings response:', {
                                    hasData: !!settingsResult.data,
                                    hasLogo: !!settings?.logo,
                                    logoLength: settings?.logo?.length
                                });

                                if (settings?.logo) {
                                    company.logo_url = settings.logo;
                                }
                                // Also get other company settings
                                if (settings?.name) company.name = company.name || settings.name;
                                // Format address object as string if needed
                                if (settings?.address && !company.address) {
                                    if (typeof settings.address === 'object') {
                                        const addr = settings.address;
                                        company.address = [addr.street, addr.city, addr.state, addr.zipCode, addr.country].filter(Boolean).join(', ');
                                    } else {
                                        company.address = settings.address;
                                    }
                                }
                            }
                        } catch (settingsError) {
                            console.warn('Could not fetch company settings:', settingsError);
                        }
                    }

                    // Fallback to localStorage
                    if (!company.logo_url) {
                        const localLogo = localStorage.getItem('companyLogo');
                        if (localLogo) {
                            company.logo_url = localLogo;
                        }
                    }

                    console.log('📋 Company data loaded for document generation:', {
                        name: company.name,
                        hasLogo: !!company.logo_url,
                        hasAddress: !!company.address
                    });

                    setCompanyData(company);
                }
            } catch (error) {
                console.error('Error fetching company data:', error);
            }
        };
        fetchCompanyData();
    }, []);

    // Handle document generation
    const handleGenerateDocument = useCallback(async (categoryId, templateId = null) => {
        if (employeeManagement.selectedEmployees.length === 0) {
            alert('Please select at least one employee');
            return;
        }

        setGenerateLoading(true);
        const template = templateId
            ? documentData.getCustomTemplatesByCategory(categoryId).find(t => t.id === templateId)
            : templateSelection.getSelectedTemplate(categoryId, documentData.templates, documentData.customTemplates);

        if (!template) {
            alert('Please select a template first');
            setGenerateLoading(false);
            return;
        }

        try {
            setGeneratingDocument({ category: categoryId, templateId, templateName: template.name });

            // Debug: Log company data state before creating generation object
            console.log('🔍 [DEBUG] companyData state:', {
                hasLogo: !!companyData?.logo_url,
                logoLength: companyData?.logo_url?.length,
                name: companyData?.name
            });

            // Get logo value with fallbacks - use String() to create a fresh copy
            const logoValue = companyData?.logo_url || companyData?.logo || localStorage.getItem('companyLogo') || '';

            // Prepare company data for PDF generation - create frozen object to prevent mutation
            const companyDataForGeneration = Object.freeze({
                name: String(companyData?.name || companyData?.company_name || localStorage.getItem('companyName') || '[Company Name]'),
                address: (() => {
                    // Handle address as object (from company settings) or string
                    if (typeof companyData?.address === 'object' && companyData.address) {
                        const addr = companyData.address;
                        return [addr.street, addr.city, addr.state, addr.zipCode, addr.country].filter(Boolean).join(', ');
                    }
                    return companyData?.address || localStorage.getItem('companyAddress') || '[Company Address]';
                })(),
                logo: logoValue ? String(logoValue) : null, // Create fresh string copy
                email: String(companyData?.email || localStorage.getItem('companyEmail') || 'hr@company.com'),
                phone: String(companyData?.phone || localStorage.getItem('companyPhone') || '+1234567890'),
                website: companyData?.website ? String(companyData.website) : null,
                location: String(companyData?.city || companyData?.location || '[Location]')
            });

            // Debug: Log the frozen object
            console.log('🔍 [DEBUG] companyDataForGeneration:', {
                hasLogo: !!companyDataForGeneration.logo,
                logoLength: companyDataForGeneration.logo?.length,
                name: companyDataForGeneration.name
            });

            console.log('🏢 Company data being sent to PDF generation:', companyDataForGeneration);

            const results = await documentGeneration.batchGenerateDocuments(
                template,
                employeeManagement.selectedEmployees,
                { delay: 500, companyData: companyDataForGeneration } // Add delay and company data
            );

            // Save to generated documents
            results.forEach(({ employee, result }) => {
                if (result.success) {
                    documentManagement.addGenerated({
                        employeeId: employee.employee_id || employee.id,
                        employeeName: `${employee.first_name} ${employee.last_name}`,
                        templateId: template.id,
                        templateName: template.name,
                        category: categoryId,
                        documentId: result.documentId,
                        pdfUrl: result.pdfUrl,
                        generatedAt: new Date().toISOString()
                    });
                }
            });

            // Clear selection after successful generation
            employeeManagement.clearEmployeeSelection();

        } catch (error) {
            console.error('Error generating documents:', error);
        } finally {
            setGenerateLoading(false);
            setGeneratingDocument(null);
        }
    }, [
        employeeManagement,
        documentGeneration,
        documentManagement,
        documentData,
        templateSelection,
        companyData
    ]);

    // Handle sending custom letter
    const handleSendCustomLetter = useCallback(async () => {
        if (employeeManagement.selectedEmployees.length === 0) {
            alert('Please select at least one employee');
            return;
        }

        if (!customLetterContent.trim()) {
            alert('Please enter letter content');
            return;
        }

        try {
            const emailData = {
                recipients: employeeManagement.selectedEmployees,
                subject: emailSubject || 'Custom Letter',
                message: emailMessage || 'Please find your custom letter attached.',
                attachments: []
            };

            const result = await emailOperations.sendEmailToEmployees(emailData);

            if (result.success) {
                // Save to generated documents
                documentManagement.addGenerated({
                    type: 'custom-letter',
                    subject: emailSubject || 'Custom Letter',
                    content: customLetterContent,
                    recipients: employeeManagement.selectedEmployees.map(emp => ({
                        id: emp.employee_id || emp.id,
                        name: `${emp.first_name} ${emp.last_name}`,
                        email: emp.email || emp.employee_email
                    })),
                    messageId: result.messageId,
                    generatedAt: new Date().toISOString()
                });

                // Reset form
                setCustomLetterContent('');
                setEmailSubject('');
                setEmailMessage('');
                employeeManagement.clearEmployeeSelection();
            }

        } catch (error) {
            console.error('Error sending custom letter:', error);
            alert('Error sending custom letter. Please try again.');
        }
    }, [
        employeeManagement.selectedEmployees,
        customLetterContent,
        emailSubject,
        emailMessage,
        emailOperations,
        documentManagement
    ]);

    // Memoized statistics
    const statistics = useMemo(() => ({
        templates: Object.values(documentData.templates).flat().length + Object.values(documentData.customTemplates).flat().length,
        selected: Object.keys(templateSelection.selectedTemplates).filter(key => templateSelection.selectedTemplates[key]?.length > 0).length,
        drafts: documentManagement.draftDocuments.length,
        generated: documentManagement.generatedDocuments.length,
        employees: employeeManagement.employees.length
    }), [
        documentData,
        templateSelection.selectedTemplates,
        documentManagement,
        employeeManagement.employees
    ]);

    // Render active tab content
    const renderTabContent = useCallback(() => {
        const commonProps = {
            theme,
            generateLoading,
            handleGenerateDocument,
            handleSendCustomLetter,
            emailSubject,
            setEmailSubject,
            emailMessage,
            setEmailMessage,
            customLetterSubject,
            setCustomLetterSubject,
            customLetterContent,
            setCustomLetterContent,
            customLetterRecipients,
            setCustomLetterRecipients,
            customLetterPreview,
            setCustomLetterPreview,
            employeeManagement,
            documentManagement,
            documentCache
        };

        switch (activeTab) {
            case 'templates':
                return (
                    <DocumentTemplatesTab
                        selectedCategory={null}
                        setSelectedCategory={() => {}}
                        selectedTemplates={templateSelection.selectedTemplates}
                        setSelectedTemplates={templateSelection.setSelectedTemplates}
                        previewTemplate={previewTemplate}
                        setPreviewTemplate={setPreviewTemplate}
                        categories={documentData.categories}
                        templates={documentData.templates}
                        customTemplates={documentData.customTemplates}
                        {...commonProps}
                    />
                );

            case 'generate':
                return (
                    <GenerateDocumentTab
                        selectedCategory={null}
                        setSelectedCategory={() => {}}
                        selectedTemplates={templateSelection.selectedTemplates}
                        setSelectedTemplates={templateSelection.setSelectedTemplates}
                        generatedDocuments={documentManagement.generatedDocuments}
                        setGeneratedDocuments={documentManagement.setGeneratedDocuments}
                        previewTemplate={previewTemplate}
                        setPreviewTemplate={setPreviewTemplate}
                        categories={documentData.categories}
                        templates={documentData.templates}
                        customTemplates={documentData.customTemplates}
                        draftDocuments={documentManagement.draftDocuments}
                        setDraftDocuments={documentManagement.setDraftDocuments}
                        editingDocument={documentGeneration.editingDocument}
                        setEditingDocument={documentGeneration.setEditingDocument}
                        setDocumentContent={documentGeneration.setDocumentContent}
                        generatingDocument={generatingDocument}
                        setGeneratingDocument={setGeneratingDocument}
                        documentGeneration={documentGeneration}
                        getCategoryName={(categoryId) =>
                            documentData.categories.find(c => c.id === categoryId)?.name || categoryId
                        }
                        {...commonProps}
                    />
                );

            case 'custom':
                return (
                    <CustomLetterTab
                        customLetterContent={customLetterContent}
                        setCustomLetterContent={setCustomLetterContent}
                        customLetterSubject={customLetterSubject}
                        setCustomLetterSubject={setCustomLetterSubject}
                        customLetterRecipients={customLetterRecipients}
                        setCustomLetterRecipients={setCustomLetterRecipients}
                        customLetterPreview={customLetterPreview}
                        setCustomLetterPreview={setCustomLetterPreview}
                        emailSubject={emailSubject}
                        setEmailSubject={setEmailSubject}
                        emailMessage={emailMessage}
                        setEmailMessage={setEmailMessage}
                        selectedEmployees={employeeManagement.selectedEmployees}
                        setSelectedEmployees={employeeManagement.setSelectedEmployees}
                        employees={employeeManagement.employees}
                        generateLoading={generateLoading}
                        handleSendCustomLetter={handleSendCustomLetter}
                        draftDocuments={documentManagement.draftDocuments}
                        setDraftDocuments={documentManagement.setDraftDocuments}
                        generatedDocuments={documentManagement.generatedDocuments}
                        setGeneratedDocuments={documentManagement.setGeneratedDocuments}
                        employeeSearchQuery={employeeManagement.searchQuery}
                        setEmployeeSearchQuery={employeeManagement.setSearchQuery}
                        departmentFilter={employeeManagement.departmentFilter}
                        setDepartmentFilter={employeeManagement.setDepartmentFilter}
                        statusFilter={employeeManagement.statusFilter}
                        setStatusFilter={employeeManagement.setStatusFilter}
                        filteredAndSortedEmployees={employeeManagement.filteredAndSortedEmployees}
                        uniqueDepartments={employeeManagement.uniqueDepartments}
                        editingDocument={documentGeneration.editingDocument}
                        setEditingDocument={documentGeneration.setEditingDocument}
                        setDocumentContent={documentGeneration.setDocumentContent}
                        setGeneratingDocument={setGeneratingDocument}
                        getCategoryName={(categoryId) =>
                            documentData.categories.find(c => c.id === categoryId)?.name || categoryId
                        }
                        {...commonProps}
                    />
                );

            default:
                return null;
        }
    }, [
        activeTab,
        theme,
        generateLoading,
        handleGenerateDocument,
        handleSendCustomLetter,
        emailSubject,
        setEmailSubject,
        emailMessage,
        setEmailMessage,
        customLetterSubject,
        setCustomLetterSubject,
        customLetterContent,
        setCustomLetterContent,
        customLetterRecipients,
        setCustomLetterRecipients,
        customLetterPreview,
        setCustomLetterPreview,
        employeeManagement,
        documentManagement,
        documentCache,
        templateSelection,
        documentData,
        previewTemplate,
        setPreviewTemplate,
        documentGeneration,
        generatingDocument,
        setGeneratingDocument
    ]);

    return (
        <ErrorBoundary theme={theme}>
            <div className={`min-h-screen p-4 sm:p-6 lg:p-8 ${
                theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
            }`}>
                {/* Header */}
                <div className={`mb-8 ${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                } rounded-xl shadow-lg p-6`}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className={`text-2xl sm:text-3xl font-bold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Document Builder
                            </h1>
                            <p className={`mt-2 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Create, manage, and generate professional documents
                            </p>
                        </div>

                        {/* Statistics */}
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 sm:gap-6">
                            <div className="text-center">
                                <div className={`text-2xl font-bold ${
                                    theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                                }`}>
                                    {statistics.templates}
                                </div>
                                <div className={`text-xs ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                    Templates
                                </div>
                            </div>
                            <div className="text-center">
                                <div className={`text-2xl font-bold ${
                                    theme === 'dark' ? 'text-green-400' : 'text-green-600'
                                }`}>
                                    {statistics.selected}
                                </div>
                                <div className={`text-xs ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                    Selected
                                </div>
                            </div>
                            <div className="text-center">
                                <div className={`text-2xl font-bold ${
                                    theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                                }`}>
                                    {statistics.drafts}
                                </div>
                                <div className={`text-xs ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                    Drafts
                                </div>
                            </div>
                            <div className="text-center">
                                <div className={`text-2xl font-bold ${
                                    theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                                }`}>
                                    {statistics.generated}
                                </div>
                                <div className={`text-xs ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                    Generated
                                </div>
                            </div>
                            <div className="text-center">
                                <div className={`text-2xl font-bold ${
                                    theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'
                                }`}>
                                    {statistics.employees}
                                </div>
                                <div className={`text-xs ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                    Employees
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tab Navigation */}
                    <div className="mt-6 flex flex-wrap gap-2" role="tablist">
                        {tabs.map(tab => (
                            <TabButton
                                key={tab.id}
                                id={tab.id}
                                label={tab.label}
                                icon={tab.icon}
                                isActive={activeTab === tab.id}
                                onClick={setActiveTab}
                                theme={theme}
                                badge={
                                    tab.id === 'drafts' ? statistics.drafts :
                                    tab.id === 'generated' ? statistics.generated :
                                    undefined
                                }
                            />
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                <div
                    id={`${activeTab}-panel`}
                    role="tabpanel"
                    aria-labelledby={`${activeTab}-tab`}
                    tabIndex={0}
                >
                    <Suspense fallback={<LoadingSpinner theme={theme} />}>
                        {renderTabContent()}
                    </Suspense>
                </div>
            </div>
        </ErrorBoundary>
    );
};

const DocumentBuilder = DocumentBuilderRefactored;
export default React.memo(DocumentBuilder);